/**
 * An article written OUTSIDE Mora's writer (by Claude in Claude Code, on the operator's behalf) is saved by
 * `import_article_revision` as an ordinary revision whose document output carries `provenance` and the
 * `importedSources` it named. These readers are the one place that shape is interpreted, so the review
 * pipeline (which retrieves those sources) and the dock (which says where the article came from) agree.
 */

export interface ImportedSourceRef {
  url: string;
  title: string;
}

export interface ImportProvenance {
  kind: 'import';
  author: string;
  note: string | null;
  importedAt: string;
}

const record = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

export function readImportProvenance(output: unknown): ImportProvenance | null {
  const provenance = record(record(output).provenance);
  if (provenance.kind !== 'import' || typeof provenance.author !== 'string') return null;
  return {
    kind: 'import',
    author: provenance.author,
    note: typeof provenance.note === 'string' ? provenance.note : null,
    importedAt: typeof provenance.importedAt === 'string' ? provenance.importedAt : '',
  };
}

/** The sources an imported article named; empty for any article that is not an import. */
export function readImportedSources(output: unknown): ImportedSourceRef[] {
  const value = record(output);
  if (!readImportProvenance(value) || !Array.isArray(value.importedSources)) return [];
  return value.importedSources.map(record).flatMap((source) =>
    typeof source.url === 'string' && /^https:\/\//.test(source.url)
      ? [
          {
            url: source.url,
            title:
              typeof source.title === 'string' && source.title.trim() ? source.title : source.url,
          },
        ]
      : [],
  );
}
